import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for ConfirmDeleteModalComponent.
 * Handles modal open/close and backdrop click dismissal.
 *
 * Usage:
 *   data-controller="application--toolkit--confirm-delete-modal-component--confirm-delete-modal-component"
 *   data-application--toolkit--confirm-delete-modal-component--confirm-delete-modal-component-modal-id-value="unique-id"
 */
export default class extends Controller {
  static targets = ["overlay"]
  static values = {
    modalId: String
  }

  declare overlayTarget: HTMLElement
  declare hasOverlayTarget: boolean
  declare modalIdValue: string

  private boundKeyHandler: ((event: KeyboardEvent) => void) | null = null

  connect(): void {
    this.boundKeyHandler = this.handleKeydown.bind(this)
  }

  disconnect(): void {
    this.removeKeyboardListener()
    this.boundKeyHandler = null
  }

  open(event: Event): void {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasOverlayTarget) {
      this.overlayTarget.removeAttribute("hidden")
      this.overlayTarget.setAttribute("aria-hidden", "false")
      document.body.style.overflow = "hidden"
      this.addKeyboardListener()

      // Focus the cancel button for accessibility
      const cancelButton = this.overlayTarget.querySelector<HTMLButtonElement>(
        '[data-action*="close"]'
      )
      if (cancelButton) {
        setTimeout(() => cancelButton.focus(), 100)
      }
    }
  }

  close(event?: Event): void {
    if (event) {
      event.preventDefault()
    }

    if (this.hasOverlayTarget) {
      this.overlayTarget.setAttribute("hidden", "")
      this.overlayTarget.setAttribute("aria-hidden", "true")
      document.body.style.overflow = ""
      this.removeKeyboardListener()
    }
  }

  backdropClick(event: Event): void {
    // Only close if clicking directly on the overlay (backdrop), not the dialog content
    if (event.target === this.overlayTarget) {
      this.close(event)
    }
  }

  confirm(_event: Event): void {
    // Let the form submission proceed naturally via Turbo
    // The modal will be removed when the page updates
    this.close()
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close()
    }
  }

  private addKeyboardListener(): void {
    if (this.boundKeyHandler) {
      document.addEventListener("keydown", this.boundKeyHandler)
    }
  }

  private removeKeyboardListener(): void {
    if (this.boundKeyHandler) {
      document.removeEventListener("keydown", this.boundKeyHandler)
    }
  }
}
